import { css } from '@styled-system/css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Message } from '@/shared/components/ui/Message';
import { Separator } from '@/shared/components/ui/Separator';

interface RenameAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  onRename: (newName: string) => Promise<void>;
  isRenaming?: boolean;
}

export const RenameAnalysisModal = ({
  isOpen,
  onClose,
  currentName,
  onRename,
  isRenaming = false,
}: RenameAnalysisModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError(null);
    }
  }, [isOpen, currentName]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Le nom de l'analyse est requis");
      return;
    }

    if (name.trim() === currentName) {
      onClose();
      return;
    }

    try {
      await onRename(name.trim());
      onClose();
    } catch (error) {
      console.error('Erreur lors du renommage:', error);
      setError("Erreur lors du renommage de l'analyse");
    }
  };

  if (!isOpen) return null;

  const overlayStyles = css({
    position: 'fixed',
    inset: 0,
    bg: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    p: '1rem',
  });

  const modalStyles = css({
    bg: 'base.bg', // #0f1012
    rounded: 'sm', // 6px from design (rounded-[10px] in Figma = ~6px in our system)
    border: '0.75px solid',
    borderColor: 'base.border', // #1e1f24
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    maxW: '25rem', // Plus petite que CreateAnalysisModal car un seul champ
    w: 'full',
    p: 0,
    overflow: 'hidden',
  });

  const headerStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pt: '1.375rem', // 22px du Figma
    pb: 'sm', // 11px pour correspondre au Figma
    px: '1.125rem', // 18px
    position: 'relative',
  });

  const titleStyles = css({
    fontSize: '0.8125rem', // 13px
    fontWeight: 'medium',
    fontFamily: 'sans',
    color: 'font.primary', // #e6e6e9
    w: 'full',
    textAlign: 'left',
    lineHeight: '1.07',
  });

  const contentStyles = css({
    px: '1.125rem', // 18px
    pb: '1.125rem',
    pt: '1.8125rem', // 29px après le separator pour correspondre exactement au Figma
  });

  const formStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '1.625rem', // 26px (approximatif entre les inputs)
  });

  const buttonGroupStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.625rem', // 10px
  });

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={overlayStyles} onClick={handleBackdropClick}>
      <div className={modalStyles} onClick={(e) => e.stopPropagation()}>
        <div className={headerStyles}>
          <h2 className={titleStyles}>{t('analysisMenu.renameTitle')}</h2>
        </div>

        <Separator />

        <div className={contentStyles}>
          <form onSubmit={handleSubmit} className={formStyles}>
            <Input
              placeholder={t('analysisMenu.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />

            {error && (
              <Message type="error" bordered>
                {error}
              </Message>
            )}

            <div className={buttonGroupStyles}>
              <Button handleClick={onClose} disabled={isRenaming}>
                Annuler
              </Button>
              <Button
                handleClick={() => handleSubmit(new Event('submit') as any)}
                showLoader
                disabled={isRenaming || !name.trim()}
              >
                Renommer
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
